import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faChevronRight, faChevronDown, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateInLA } from "../../utils/date";
import { utcToLADateKey, laDateKeyToUTC } from "../../utils/laDateUtils";

const BookingSummaryTable = ({ bookings }) => (
  <table className="table table-sm mb-0 allocation-booking-summary">
    <thead>
      <tr>
        <th>Hotel</th>
        <th>Room Type</th>
        <th>Room</th>
        <th>Check-in</th>
        <th>Check-out</th>
      </tr>
    </thead>
    <tbody>
      {bookings.map((b, i) => (
        <tr key={i}>
          <td>{b.hotel_name}</td>
          <td>{b.room_type}</td>
          <td>{b.room_label}</td>
          <td>{formatDateInLA(laDateKeyToUTC(b.from_date))}</td>
          <td>{b.from_date === b.to_date ? "—" : formatDateInLA(laDateKeyToUTC(b.to_date))}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ParticipantRow = ({
  person,
  selected,
  onSelect,
  isAvailable,
  isEditing,
  onEditToggle,
  onUpdate,
  onDateChange,
  isExpanded,
  onExpandToggle,
  expandedBookings,
  expandLoading,
  colCount,
  selectionLocked = false,
}) => {
  const canSelect = isAvailable && !selectionLocked;
  const rowClick = canSelect ? onSelect : undefined;

  const displayArrival = person.arrival_la_date_key
    ? formatDateInLA(laDateKeyToUTC(person.arrival_la_date_key))
    : person.arrivalDisplay
    ? formatDateInLA(person.arrivalDisplay)
    : "—";

  const displayDeparture = person.departure_la_date_key
    ? formatDateInLA(laDateKeyToUTC(person.departure_la_date_key))
    : person.departureDisplay
    ? formatDateInLA(person.departureDisplay)
    : "—";

  return (
    <React.Fragment>
      <tr
        className={!isEditing && !isAvailable ? "participant-row--unavailable" : undefined}
        style={{
          cursor: canSelect ? "pointer" : "default",
          background: selectionLocked && selected ? "#faf3e0" : selected ? "#e8f5e9" : undefined,
          opacity: isEditing ? 1 : selectionLocked ? (selected ? 1 : 0.45) : 1,
          ...(!isEditing && !isAvailable ? { '--bs-table-striped-bg': '#fff', '--bs-table-bg': '#fff' } : {}),
        }}
        onClick={rowClick}
        title={selectionLocked ? undefined : (isAvailable ? undefined : "Outside arrival/departure window (LA)")}
      >
        <td
          style={{ textAlign: "center", verticalAlign: "middle", width: "30px" }}
          onClick={(e) => { e.stopPropagation(); onExpandToggle(); }}
        >
          <FontAwesomeIcon
            icon={isExpanded ? faChevronDown : faChevronRight}
            className="allocation-expand-chevron"
          />
        </td>

        <td style={{ textAlign: "center" }}>
          <input
            type="checkbox"
            checked={selected}
            onChange={rowClick || (() => {})}
            disabled={!canSelect}
            style={{ accentColor: "#666" }}
          />
        </td>

        <td>
          {person.fullName}
          <div className="text-muted small">{person.organisation || "—"}</div>
          {!isAvailable && (
            <div style={{ fontSize: "11px", color: "#b00", fontStyle: "italic" }}>
              Outside stay window (LA)
            </div>
          )}
        </td>

        <td onClick={(e) => isEditing && e.stopPropagation()}>
          {isEditing ? (
            <DatePicker
              selected={person.arrival_la_date_key ? laDateKeyToUTC(person.arrival_la_date_key) : null}
              onChange={(d) => onDateChange(person._id, "arrival_la_date_key", utcToLADateKey(d))}
              dateFormat="dd-MMM-yyyy"
              className="form-control form-control-sm"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChangeRaw={(e) => e.preventDefault()}
              placeholderText="Arrival (LA)"
            />
          ) : (
            displayArrival
          )}
        </td>

        <td onClick={(e) => isEditing && e.stopPropagation()}>
          {isEditing ? (
            <DatePicker
              selected={person.departure_la_date_key ? laDateKeyToUTC(person.departure_la_date_key) : null}
              onChange={(d) => onDateChange(person._id, "departure_la_date_key", utcToLADateKey(d))}
              dateFormat="dd-MMM-yyyy"
              className="form-control form-control-sm"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChangeRaw={(e) => e.preventDefault()}
              placeholderText="Departure (LA)"
            />
          ) : (
            displayDeparture
          )}
        </td>

        <td style={{ textAlign: "center", verticalAlign: "middle" }} onClick={(e) => e.stopPropagation()}>
          {isEditing ? (
            <div className="allocation-participant-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ backgroundColor: "#BFA253", color: "#ffffff", borderColor: "#BFA253" }}
                onClick={() => onUpdate(person)}
              >
                Update
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={onEditToggle}
                title="Cancel edit"
              >
                Cancel
              </button>
            </div>
          ) : (
            <span
              onClick={onEditToggle}
              style={{ cursor: "pointer", color: "#000", fontSize: "12px" }}
              title="Edit arrival/departure"
            >
              <FontAwesomeIcon icon={faPen} />
            </span>
          )}
        </td>
      </tr>

      {isExpanded && (
        <tr className="allocation-expanded-row">
          <td colSpan={colCount || 6} style={{ padding: 0 }}>
            <div className="allocation-expanded-content">
              {expandLoading && !expandedBookings ? (
                <div style={{ textAlign: "center", padding: "12px", color: "#666" }}>
                  <FontAwesomeIcon icon={faCircleNotch} spin /> Loading bookings…
                </div>
              ) : !expandedBookings || expandedBookings.length === 0 ? (
                <div style={{ textAlign: "center", padding: "12px", color: "#888", fontStyle: "italic" }}>
                  No existing bookings
                </div>
              ) : (
                <BookingSummaryTable bookings={expandedBookings} />
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default ParticipantRow;
